import {
    Box, List, ListItemButton, ListItemIcon, ListItemText, MenuItem, Radio, TextField,
    ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import ChevronRight from "@mui/icons-material/ChevronRight";
import RepeatIcon from "@mui/icons-material/Repeat";
import { ReactNode, useState } from "react";
import {
    emptyRepeat, MonthlyMode, PeriodicMode, QuarterlyMode, Repeat, RepeatKind, YearlyMode,
} from "../../../api/Tasks";
import {
    describeRepeat, monthNames, nthOptions, seedRepeat, weekdayNames, weekdayShortNames,
} from "../repeatText";
import { gutter, MobileCard, MobileSheet } from "./MobileSheet";

// the kinds are named for a reader rather than for the schema, since the picker is
// a list of plain choices rather than a form (see repeatKinds for the dense version)
const kinds: { value: RepeatKind; label: string; hint: string }[] = [
    { value: "", label: "Does not repeat", hint: "" },
    { value: "daily", label: "Every day", hint: "" },
    { value: "weekly", label: "Every week", hint: "On the days you pick" },
    { value: "monthly", label: "Every month", hint: "" },
    { value: "quarterly", label: "Every 3 months", hint: "" },
    { value: "yearly", label: "Every year", hint: "" },
    { value: "periodically", label: "After it's done", hint: "Counted from the last one" },
];

// MobileRepeatField is a full-width row that says whether a task repeats, and opens a
// page to change it on.
// It's a page rather than the desktop panel's inline form because a phone has no
// room to keep the task and the repeat on screen at once (see RepeatDetails).
export function MobileRepeatField(props: {
    value: Repeat;
    dueDate: string | null;
    onChange: (value: Repeat) => void;
}) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <ListItemButton onClick={() => setOpen(true)} sx={{ px: `${gutter}px` }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                    <RepeatIcon sx={{ color: props.value.kind ? "success.main" : "action.active" }} />
                </ListItemIcon>
                <ListItemText primary="Repeat"
                              secondary={describeRepeat(props.value) || "Does not repeat"} />
                <ChevronRight color="action" />
            </ListItemButton>

            {/* re-keyed per opening, so the page starts from what the task carries now */}
            {open && (
                <RepeatSheet
                    key={String(open)}
                    value={props.value}
                    dueDate={props.dueDate}
                    onClose={next => {
                        setOpen(false);
                        props.onChange(next);
                    }}
                />
            )}
        </>
    );
}

// RepeatSheet edits a copy and hands it back on the way out, so fiddling with the
// options costs one save rather than one per tap
export function RepeatSheet(props: {
    value: Repeat;
    dueDate: string | null;
    onClose: (value: Repeat) => void;
}) {
    const [draft, setDraft] = useState(props.value);

    const update = (changes: Partial<Repeat>) => setDraft({ ...draft, ...changes });

    // a kind is set up from scratch, so what the one before it was given doesn't
    // carry over into a cycle it has nothing to do with
    const pickKind = (kind: RepeatKind) => {
        if (kind === draft.kind) return;
        setDraft(seedRepeat({ ...emptyRepeat, kind: kind }, {}, props.dueDate));
    };

    const seed = (changes: {
        monthlyMode?: MonthlyMode;
        quarterlyMode?: QuarterlyMode;
        yearlyMode?: YearlyMode;
        periodicMode?: PeriodicMode;
    }) => setDraft(seedRepeat(draft, changes, props.dueDate));

    return (
        <MobileSheet
            title={<Typography variant="subtitle1">Repeat</Typography>}
            onClose={() => props.onClose(draft.kind ? draft : emptyRepeat)}
        >
            <MobileCard>
                <List disablePadding>
                    {kinds.map(kind => (
                        <ListItemButton key={kind.value} onClick={() => pickKind(kind.value)}>
                            <Radio checked={draft.kind === kind.value} edge="start" sx={{ mr: 0.5 }} />
                            <ListItemText primary={kind.label} secondary={kind.hint || undefined} />
                        </ListItemButton>
                    ))}
                </List>
            </MobileCard>

            <Details value={draft} onUpdate={update} onSeed={seed} />
        </MobileSheet>
    );
}

// Details is the part of the repeat the chosen kind still needs answered. Nothing
// shows for a kind that needs nothing.
function Details(props: {
    value: Repeat;
    onUpdate: (changes: Partial<Repeat>) => void;
    onSeed: (changes: {
        monthlyMode?: MonthlyMode;
        quarterlyMode?: QuarterlyMode;
        yearlyMode?: YearlyMode;
        periodicMode?: PeriodicMode;
    }) => void;
}) {
    const repeat = props.value;

    if (repeat.kind === "weekly") {
        return (
            <MobileCard inset>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ pb: 1 }}>
                    Repeat on
                </Typography>

                <ToggleButtonGroup
                    fullWidth
                    value={repeat.weekdays}
                    // a weekly repeat with no day never comes round again, so the last
                    // one standing can't be turned off
                    onChange={(_, days: number[]) => days.length > 0
                        && props.onUpdate({ weekdays: [...days].sort((a, b) => a - b) })}
                >
                    {weekdayShortNames.map((name, day) => (
                        <ToggleButton key={day} value={day} aria-label={weekdayNames[day]}>
                            {name}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </MobileCard>
        );
    }

    // monthly and quarterly pick the day the same way; they differ only in which month
    // it's picked out of (see core/task/repeat.go)
    if (repeat.kind === "monthly" || repeat.kind === "quarterly") {
        const quarterly = repeat.kind === "quarterly";
        const mode = quarterly ? repeat.quarterlyMode : repeat.monthlyMode;
        const pick = (value: MonthlyMode) => props.onSeed(quarterly
            ? { quarterlyMode: value }
            : { monthlyMode: value });

        return (
            <MobileCard>
                <Choice checked={mode === "day-of-month"} label="On a day of the month"
                        onPick={() => pick("day-of-month")}>
                    <DayOfMonth value={repeat.dayOfMonth} onChange={props.onUpdate} />
                </Choice>

                <Choice checked={mode === "nth-day-of-week"} label="On a weekday"
                        onPick={() => pick("nth-day-of-week")}>
                    <NthDayOfWeek value={repeat} onChange={props.onUpdate} />
                </Choice>
            </MobileCard>
        );
    }

    if (repeat.kind === "yearly") {
        return (
            <MobileCard>
                <Choice checked={repeat.yearlyMode === "same-date"} label="On the same date"
                        onPick={() => props.onSeed({ yearlyMode: "same-date" })} />

                <Choice checked={repeat.yearlyMode === "nth-day-of-week"} label="On a weekday"
                        onPick={() => props.onSeed({ yearlyMode: "nth-day-of-week" })}>
                    <NthDayOfWeek value={repeat} onChange={props.onUpdate} />
                    <TextField select fullWidth label="Month" sx={{ mt: 2 }}
                               value={repeat.month}
                               onChange={e => props.onUpdate({ month: parseInt(e.target.value) })}>
                        {monthNames.map((name, index) => (
                            <MenuItem key={name} value={index + 1}>{name}</MenuItem>
                        ))}
                    </TextField>
                </Choice>
            </MobileCard>
        );
    }

    if (repeat.kind === "periodically") {
        return (
            <MobileCard>
                <Box sx={{ px: `${gutter}px`, pt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                        Counted from the completed task's due date, or the day it was
                        completed when it had none.
                    </Typography>
                </Box>

                <Choice checked={repeat.periodicMode === "days"} label="A number of days later"
                        onPick={() => props.onSeed({ periodicMode: "days" })}>
                    <TextField
                        fullWidth
                        label="Days"
                        type="number"
                        value={repeat.days}
                        onChange={e => props.onUpdate({ days: parseInt(e.target.value) || 0 })}
                        slotProps={{ htmlInput: { min: 1, inputMode: "numeric" } }}
                    />
                </Choice>

                <Choice checked={repeat.periodicMode === "nth-day-of-week"} label="On the next weekday"
                        onPick={() => props.onSeed({ periodicMode: "nth-day-of-week" })}>
                    {/* "last" needs a period to be the last of, and this one counts
                        forward from the day the task was done (see repeat.go) */}
                    <NthDayOfWeek value={repeat} allowLast={false} onChange={props.onUpdate} />
                </Choice>
            </MobileCard>
        );
    }

    return null;
}

// Choice is one of the ways a kind can be set up: a row that picks it, and what it
// needs answered underneath, shown only while it's the one picked
function Choice(props: {
    checked: boolean;
    label: string;
    onPick: () => void;
    children?: ReactNode;
}) {
    return (
        <>
            <ListItemButton onClick={props.onPick}>
                <Radio checked={props.checked} edge="start" sx={{ mr: 0.5 }} />
                <ListItemText primary={props.label} />
            </ListItemButton>

            {props.checked && props.children && (
                <Box sx={{ px: `${gutter}px`, pb: 2 }}>{props.children}</Box>
            )}
        </>
    );
}

function DayOfMonth(props: { value: number; onChange: (changes: Partial<Repeat>) => void }) {
    return (
        <TextField
            fullWidth
            label="Day"
            type="number"
            value={props.value}
            onChange={e => props.onChange({ dayOfMonth: parseInt(e.target.value) || 0 })}
            slotProps={{ htmlInput: { min: 1, max: 31, inputMode: "numeric" } }}
        />
    );
}

function NthDayOfWeek(props: {
    value: Repeat;
    allowLast?: boolean;
    onChange: (changes: Partial<Repeat>) => void;
}) {
    const options = props.allowLast === false ? nthOptions.filter(o => o.value > 0) : nthOptions;

    return (
        <Box sx={{ display: "flex", gap: 1 }}>
            <TextField select label="Which" sx={{ width: 110 }}
                       value={props.value.nth}
                       onChange={e => props.onChange({ nth: parseInt(e.target.value) })}>
                {options.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
            </TextField>

            <TextField select label="Day" sx={{ flex: 1 }}
                       value={props.value.weekday}
                       onChange={e => props.onChange({ weekday: parseInt(e.target.value) })}>
                {weekdayNames.map((name, day) => (
                    <MenuItem key={name} value={day}>{name}</MenuItem>
                ))}
            </TextField>
        </Box>
    );
}
